import { AnimatePresence, motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "@/lib/cart";
import { api, formatApiError, formatPrice } from "@/lib/api";

export default function CartDrawer() {
  const {
    items, open, setOpen, removeItem, updateQty, subtotal, count, clear,
    liveCount, liveSubtotal, liveItems, lastCollection,
  } = useCart();
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState("");
  const [applied, setApplied] = useState(null); // { code, type, amount, discount(cents) }
  const [promoErr, setPromoErr] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const navigate = useNavigate();

  const liveSubtotalCents = Math.round(liveSubtotal * 100);
  const discountCents = applied ? Math.min(applied.discount, liveSubtotalCents) : 0;
  const finalCents = Math.max(0, liveSubtotalCents - discountCents);

  const applyPromo = async () => {
    const code = promo.trim();
    if (!code || promoBusy) return;
    setPromoBusy(true);
    setPromoErr("");
    try {
      const { data } = await api.post("/discounts/validate", { code, subtotal: liveSubtotalCents });
      setApplied({ code: data.code, type: data.type, amount: data.amount, discount: data.discount });
      toast.success(`Code ${data.code} applied`);
    } catch (err) {
      setApplied(null);
      setPromoErr(formatApiError(err.response?.data?.detail));
    } finally {
      setPromoBusy(false);
    }
  };

  // Re-validate the applied code whenever the live subtotal changes so a
  // now-invalid code (e.g. below its min-order) is auto-removed instead of
  // silently failing at checkout.
  useEffect(() => {
    if (!applied) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/discounts/validate", { code: applied.code, subtotal: liveSubtotalCents });
        if (!cancelled) setApplied((a) => (a ? { ...a, amount: data.amount, discount: data.discount } : a));
      } catch (err) {
        if (!cancelled) {
          setApplied(null);
          setPromoErr(formatApiError(err.response?.data?.detail));
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveSubtotalCents]);

  const removePromo = () => {
    setApplied(null);
    setPromo("");
    setPromoErr("");
  };

  const continueShopping = () => {
    const dest = lastCollection || items[items.length - 1]?.collection;
    setOpen(false);
    navigate(dest ? `/shop/${dest}` : "/");
  };

  const handleCheckout = async () => {
    if (liveCount === 0 || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: liveItems.map((i) => ({ slug: i.slug, size: i.size, quantity: i.qty })),
        origin_url: window.location.origin,
        discount_code: applied ? applied.code : undefined,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[70] bg-[#090909]/70"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            className="fixed top-0 right-0 bottom-0 z-[71] w-full max-w-md bg-[#F2EFEB] text-[#090909] border-l-2 border-[#090909] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-5 border-b-2 border-[#090909]">
              <h3 className="font-display text-3xl leading-none">
                YOUR BAG <span className="text-[#FF2A00]">({count})</span>
              </h3>
              <button
                onClick={() => setOpen(false)}
                data-testid="cart-close-btn"
                className="font-mono-ui text-sm uppercase hover:text-[#FF2A00]"
              >
                Close ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar">
              {items.length === 0 ? (
                <div className="p-8 font-mono-ui text-sm opacity-70" data-testid="cart-empty">
                  Your bag is empty. Go stack some grief.
                </div>
              ) : (
                items.map((i) => (
                  <div
                    key={i.id}
                    data-testid={`cart-item-${i.slug}`}
                    className="flex gap-4 p-4 border-b border-[#090909]"
                  >
                    <div className="w-20 h-24 border-2 border-[#090909] overflow-hidden shrink-0">
                      {i.image && (
                        <img src={i.image} alt={i.name} className="w-full h-full object-cover grayscale" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2">
                        <p className="font-mono-ui text-sm uppercase leading-tight">{i.name}</p>
                        <button
                          onClick={() => removeItem(i.id)}
                          data-testid={`cart-remove-${i.slug}`}
                          className="font-mono-ui text-xs opacity-50 hover:opacity-100 hover:text-[#FF2A00]"
                        >
                          remove
                        </button>
                      </div>
                      <p className="font-mono-ui text-xs opacity-60 mt-1">Size {i.size}</p>
                      {i.status !== "live" && (
                        <span
                          data-testid={`cart-comingsoon-tag-${i.slug}`}
                          className="inline-block mt-1 font-mono-ui text-[10px] uppercase tracking-widest bg-[#DFFF00] text-[#090909] px-1.5 py-0.5"
                        >
                          Coming soon · waitlist
                        </span>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border-2 border-[#090909]">
                          <button onClick={() => updateQty(i.id, -1)} data-testid={`cart-qty-dec-${i.slug}`} className="w-8 h-8 hover:bg-[#DFFF00]">−</button>
                          <span className="w-8 text-center font-mono-ui text-sm" data-testid={`cart-qty-${i.slug}`}>{i.qty}</span>
                          {(() => {
                            const atCap = i.status === "live" && !i.allowBackorder && i.qty >= (i.maxStock ?? Infinity);
                            return (
                              <button
                                onClick={() => updateQty(i.id, 1)}
                                disabled={atCap}
                                data-testid={`cart-qty-inc-${i.slug}`}
                                className="w-8 h-8 hover:bg-[#DFFF00] disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                              >+</button>
                            );
                          })()}
                        </div>
                        <span className="font-mono-ui text-sm">${formatPrice(i.price * i.qty)}</span>
                      </div>
                      {i.status === "live" && !i.allowBackorder && i.qty >= (i.maxStock ?? Infinity) && (
                        <p className="font-mono-ui text-[0.65rem] text-[#FF2A00] mt-1" data-testid={`cart-max-stock-${i.slug}`}>Max stock reached ({i.maxStock})</p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="border-t-2 border-[#090909] p-5">
              {liveCount > 0 && (
                <div className="mb-4" data-testid="promo-section">
                  {applied ? (
                    <div className="flex items-center justify-between border-2 border-[#090909] bg-[#DFFF00] px-3 py-2" data-testid="promo-applied">
                      <span className="font-mono-ui text-xs uppercase tracking-widest">
                        Code {applied.code} · {applied.type === "percent" ? `${applied.amount}% off` : `$${formatPrice(applied.amount)} off`}
                      </span>
                      <button onClick={removePromo} data-testid="promo-remove-btn" className="font-mono-ui text-xs uppercase underline underline-offset-2 hover:text-[#FF2A00]">Remove</button>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2">
                        <input
                          value={promo}
                          onChange={(e) => setPromo(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                          placeholder="Promo code"
                          data-testid="promo-input"
                          className="flex-1 h-10 bg-transparent border-2 border-[#090909] px-3 font-mono-ui text-sm uppercase tracking-widest focus:outline-none focus:border-[#FF2A00]"
                        />
                        <button
                          onClick={applyPromo}
                          disabled={promoBusy || !promo.trim()}
                          data-testid="promo-apply-btn"
                          className="h-10 px-4 border-2 border-[#090909] font-mono-ui text-xs uppercase tracking-widest hover:bg-[#090909] hover:text-[#F2EFEB] transition-colors disabled:opacity-40"
                        >
                          {promoBusy ? "..." : "Apply"}
                        </button>
                      </div>
                      {promoErr && <p className="mt-2 font-mono-ui text-xs text-[#FF2A00]" data-testid="promo-error">{promoErr}</p>}
                    </>
                  )}
                </div>
              )}

              {liveCount > 0 && applied && discountCents > 0 && (
                <div className="flex justify-between font-mono-ui text-sm mb-2">
                  <span className="uppercase tracking-widest">Subtotal</span>
                  <span>${formatPrice(liveSubtotalCents / 100)}</span>
                </div>
              )}
              {liveCount > 0 && applied && discountCents > 0 && (
                <div className="flex justify-between font-mono-ui text-sm mb-2 text-[#FF2A00]" data-testid="cart-discount-line">
                  <span className="uppercase tracking-widest">Discount ({applied.code})</span>
                  <span>−${formatPrice(discountCents / 100)}</span>
                </div>
              )}
              <div className="flex justify-between font-mono-ui text-sm mb-4">
                <span className="uppercase tracking-widest">
                  {liveCount > 0 ? (applied && discountCents > 0 ? "Total" : "Available now") : "Subtotal"}
                </span>
                <span data-testid="cart-subtotal">
                  ${formatPrice(liveCount > 0 ? finalCents / 100 : subtotal)}
                </span>
              </div>

              {liveCount > 0 ? (
                <>
                  <button
                    onClick={handleCheckout}
                    disabled={loading}
                    data-testid="cart-checkout-btn"
                    className="w-full h-12 border-2 border-[#090909] bg-[#090909] text-[#DFFF00] font-mono-ui uppercase text-sm tracking-widest hover:bg-[#FF2A00] hover:border-[#FF2A00] hover:text-[#F2EFEB] transition-colors disabled:opacity-60 disabled:cursor-wait"
                  >
                    {loading ? "Redirecting…" : `Checkout — $${formatPrice(finalCents / 100)} →`}
                  </button>
                  {count > liveCount && (
                    <p className="mt-3 font-mono-ui text-[11px] leading-relaxed opacity-60" data-testid="cart-mixed-note">
                      Coming-soon items stay in your bag as waitlist picks and aren’t part of this checkout.
                    </p>
                  )}
                  <p className="mt-2 font-mono-ui text-[10px] uppercase tracking-widest opacity-40 text-center">
                    Secured by Stripe · tax at checkout
                  </p>
                </>
              ) : (
                <>
                  <div className="bg-[#090909] text-[#DFFF00] p-4 font-mono-ui text-xs uppercase tracking-widest text-center mb-3" data-testid="cart-comingsoon-note">
                    Drop 01 — Checkout opens at launch
                  </div>
                  <button
                    disabled
                    data-testid="cart-checkout-btn"
                    className="w-full h-12 border-2 border-[#090909] bg-[#090909]/40 text-[#F2EFEB] font-mono-ui uppercase text-sm tracking-widest cursor-not-allowed"
                  >
                    Checkout — Coming Soon
                  </button>
                </>
              )}

              <button
                onClick={continueShopping}
                data-testid="cart-continue-shopping-btn"
                className="w-full mt-3 h-11 border-2 border-[#090909] font-mono-ui uppercase text-xs tracking-widest hover:bg-[#090909] hover:text-[#F2EFEB] transition-colors"
              >
                ← Continue shopping
              </button>

              {items.length > 0 && (
                <button
                  onClick={clear}
                  data-testid="cart-clear-btn"
                  className="w-full mt-3 font-mono-ui text-xs uppercase opacity-50 hover:opacity-100 hover:text-[#FF2A00]"
                >
                  Clear bag
                </button>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
